import { create } from 'zustand';
import { getProjectsScheme, getProjectSchemeById, getAssociatedProjects, hasProjectAccess } from '../v2/apis/projectsSchemeApi';
import { BoardSchemeInterface, ProjectSchemeInterface } from 'ts-interfaces';
import { CollectionInterface, Permissions } from 'ts-interfaces';
import { getBoardsForProject, getBoardsForProjectOrThrow, getBoardsForProjects } from '../v2/apis/boardsSchemeApi';
import { getCollectionsForProject, getCollectionsForProjects } from '../v2/apis/collectionsSchemeApi';
import { docBelongsToActiveWorkspace, firebaseHelper, getActiveDesignFirmId, useWorkspaceStore } from "ui";
import { onSharingV2Flip } from 'ui/utils/workspace/sharingV2';
import { ensureSharingV2Flag } from 'ui/utils/useDesignFirm';
import { activeTeamScope, awaitActiveTeamScope, projectTeamScope, teamScopeKnown } from '../v2/apis/accessQueries';
import { getDesignFirmsOfUser, getDesignersWithAccess } from '../v2/apis/teamsApi';
import { teamAnalytics, teammateAccess, teammateItem } from '../team/teamAnalytics';

/**
 * Workspace ownership check for a project (Phase A.3.3).
 *
 * Personal workspace: only projects where design_firm_id is null/absent.
 * Team workspace: only projects where design_firm_id === active firm id.
 * Pre-migration projects (no design_firm_id) default to Personal.
 *
 * Thin wrapper over the shared `docBelongsToActiveWorkspace` so the predicate
 * lives in one place; kept as a named export for call sites and tests.
 */
export function projectBelongsToActiveWorkspace(
  project: { design_firm_id?: string | null },
  userId: string | null,
): boolean {
  return docBelongsToActiveWorkspace(project, userId);
}

/**
 * Derive ownedProjects / sharedProjects from the full project list, keyed on
 * the active workspace.
 *
 * - Team workspace: every project stamped with the active firm id, regardless
 *   of `owner` (so teammates' projects show).
 * - Personal workspace: owner === uid. Personal is owner-only since the
 *   cutover, so sharedProjects is always empty (kept until PR6).
 */
function deriveProjectLists(
  projects: ProjectSchemeInterface[],
  uid: string | null,
  _sharedProjectIds: string[],
) {
  const activeProjects = projects.filter((p) => !p.archive);
  const teamFirmId = getActiveDesignFirmId(uid);
  if (teamFirmId) {
    const ownedProjects = activeProjects.filter((p) =>
      projectBelongsToActiveWorkspace(p, uid),
    );
    return { activeProjects, ownedProjects, sharedProjects: [] as ProjectSchemeInterface[] };
  }
  const ownedProjects = activeProjects
    .filter((p) => p.owner === uid)
    .filter((p) => projectBelongsToActiveWorkspace(p, uid));
  return { activeProjects, ownedProjects, sharedProjects: [] as ProjectSchemeInterface[] };
}

interface ProjectsState {
  projects: ProjectSchemeInterface[];
  activeProjects: ProjectSchemeInterface[];
  ownedProjects: ProjectSchemeInterface[];
  sharedProjects: ProjectSchemeInterface[];
  isProjectsLoading: boolean;
  projectsLoaded: boolean;
  loadedUid: string | null;
  sharedProjectIds: string[];

  invalidateCache: () => void;
  setProject: (project: ProjectSchemeInterface) => void;
  setProjects: (projects: ProjectSchemeInterface[]) => void;
  addProject: (project: ProjectSchemeInterface) => void;
  setSharedProjectIds: (ids: string[]) => void;
  getProjectOwner: (projectId: string) => string | undefined;
  getProjectSchemas: (uid: string, sharedProjectIds?: string[], permissions?: any) => Promise<void>;
  updateBoardInProjects: (board: BoardSchemeInterface) => void;
  removeBoardFromProjects: (boardId: string) => void;
  updateCollectionInProjects: (collection: CollectionInterface) => void;
  removeCollectionFromProjects: (collectionId: string) => void;
}

interface ProjectDetails {
  project: ProjectSchemeInterface;
  boards: BoardSchemeInterface[];
  collections: CollectionInterface[];
  isLoading: boolean;
}

interface ProjectSchemaDetailsState {
  projectsData: Record<string, ProjectDetails>;
  loadedUids: Record<string, string>; 
  userProjects: ProjectSchemeInterface[];
  ownedProjects: ProjectSchemeInterface[];
  sharedProjects: ProjectSchemeInterface[];
  setBoards: (projectId: string, boards: BoardSchemeInterface[], uid: string, permissions: Permissions | null) => Promise<void>;
  refreshBoards: (projectId: string, uid: string, permissions: Permissions | null) => Promise<void>;
  setCollections: (projectId: string, collections: CollectionInterface[], uid: string, permissions: Permissions | null) => Promise<void>;
  setProject: (projectId: string, project: ProjectSchemeInterface) => void;
  getProjectDetails: (project: ProjectSchemeInterface, uid: string, permissions: Permissions | null) => Promise<void>;
  getProjectById: (projectId: string) => ProjectDetails | null;
  syncUserProjects: () => void;
}

interface ProjectManagementState {
  selectedProject: ProjectSchemeInterface | null;
  designFirms: any[];
  teamMembers: any[];
  associatedProjectsList: any[];
  error: string | null;
  setProjectData: (data: Partial<ProjectManagementState>) => void;
  resetProjectData: () => void;
  fetchProjectDataById: (id: string, uid: string, permissions: Permissions | null) => Promise<void>;
}

// Sync changes to useProjectSchemaDetails. Its boards are full docs while the
// list store holds summaries (BOARD_SUMMARY_FIELDS), so only the changed board
// is copied across, never a list-store array.
const upsertBoardInDetails = (board: BoardSchemeInterface) => {
  const { projectsData } = useProjectSchemaDetails.getState();
  const next = { ...projectsData };
  let changed = false;
  for (const [projectId, details] of Object.entries(projectsData)) {
    const boards = details.boards || [];
    const belongsHere = projectId === board.associatedProjectId && board.archive !== true;
    const present = boards.some(b => b.id === board.id);
    if (!belongsHere && !present) continue;
    next[projectId] = {
      ...details,
      boards: !belongsHere
        ? boards.filter(b => b.id !== board.id)
        : present
          ? boards.map(b => (b.id === board.id ? board : b))
          : [...boards, board],
    };
    changed = true;
  }
  if (changed) useProjectSchemaDetails.setState({ projectsData: next });
};

const removeBoardFromDetails = (boardId: string) => {
  const { projectsData } = useProjectSchemaDetails.getState();
  const next = { ...projectsData };
  let changed = false;
  for (const [projectId, details] of Object.entries(projectsData)) {
    if (!details.boards?.some(b => b.id === boardId)) continue;
    next[projectId] = { ...details, boards: details.boards.filter(b => b.id !== boardId) };
    changed = true;
  }
  if (changed) useProjectSchemaDetails.setState({ projectsData: next });
};

const syncCollectionsToDetails = (projectId: string, updatedCollections: CollectionInterface[]) => {
  if (!useProjectSchemaDetails.getState().projectsData[projectId]) return;
  useProjectSchemaDetails.setState(state => ({
    projectsData: {
      ...state.projectsData,
      [projectId]: { ...state.projectsData[projectId], collections: updatedCollections }
    }
  }));
};

export const useProjectSchemas = create<ProjectsState>((set, get) => ({
  projects: [],
  activeProjects: [],
  ownedProjects: [],
  sharedProjects: [],
  isProjectsLoading: false,
  projectsLoaded: false,
  loadedUid: null,
  sharedProjectIds: [],

  invalidateCache: () => {
    set({ loadedUid: null, projectsLoaded: false });
  },
  
  setProject: (project) => {
    set((state) => {
      const projects = state.projects.map(p =>
        p.id === project.id ? project : p
      );
      return {
        projects,
        ...deriveProjectLists(projects, state.loadedUid, state.sharedProjectIds),
      };
    });
  },
  
  setProjects: (projects) =>
    set((state) => ({
      projects,
      ...deriveProjectLists(projects, state.loadedUid, state.sharedProjectIds),
    })),

  addProject: (project: ProjectSchemeInterface) => {
    set((state) => {
      // New projects start with empty boards array
      const newProject = { ...project, boards: [], collections: [] };
      const newProjects = [newProject, ...state.projects];
      return {
        projects: newProjects,
        ...deriveProjectLists(newProjects, state.loadedUid, state.sharedProjectIds),
      };
    });
  },

  setSharedProjectIds: (ids) => set({ sharedProjectIds: ids }),

  // Helper function to get project owner by project ID
  getProjectOwner: (projectId: string): string | undefined => {
    const { projects } = get();
    return projects.find(p => p.id === projectId)?.owner;
  },

  
  getProjectSchemas: async (uid: string, sharedProjectIds: string[] = [], permissions?: any) => {
    const { loadedUid, isProjectsLoading } = get();

    // Check if we need to reload
    const currentSharedIds = get().sharedProjectIds;
    const sharedIdsChanged = JSON.stringify(sharedProjectIds.sort()) !== JSON.stringify(currentSharedIds.sort());
    
    if (!uid || (uid === loadedUid && !sharedIdsChanged) || isProjectsLoading) return;
    
    try {
      set({ isProjectsLoading: true });

      // Team workspace → fetch projects stamped with the firm id (all members').
      // Personal → unchanged owner-scoped + ACL-shared fetch. A flagged team
      // (sharingV2) fetches only what the user can read.
      const teamFirmId = getActiveDesignFirmId(uid);
      const teamScope = teamScopeKnown(uid)
        ? activeTeamScope(uid)
        : await awaitActiveTeamScope(uid);

      // Fetch projects (without boards)
      const projectSchemas = await getProjectsScheme(uid, sharedProjectIds, teamFirmId, teamScope);

      // Fetch boards for all projects
      const projectIds = projectSchemas.map(p => p.id);
      const [boardsByProject, collectionsByProject] = await Promise.all([
        getBoardsForProjects(projectIds, uid, permissions, teamScope),
        getCollectionsForProjects(projectIds, uid, permissions, teamScope)
      ]);

      // Add boards and collections to each project
      const projectsWithBoardsAndCollections = projectSchemas.map(project => ({
        ...project,
        boards: boardsByProject.get(project.id) || [],
        collections: collectionsByProject.get(project.id) || []
      }));

      set({
        projects: projectsWithBoardsAndCollections,
        ...deriveProjectLists(projectsWithBoardsAndCollections, uid, sharedProjectIds),
        isProjectsLoading: false,
        projectsLoaded: true,
        loadedUid: uid,
        sharedProjectIds
      });
      // The workspace changed or the team's flag landed mid-fetch (a switch
      // skips its own refetch until this load lands): the query changed.
      if (getActiveDesignFirmId(uid) !== teamFirmId || !!activeTeamScope(uid) !== !!teamScope) refetchProjects();
    } catch (error) {
      console.error('Error fetching project schemas:', error);
      set({ isProjectsLoading: false });
    }
  },
  
  // Update a board across all projects (for when board is renamed, thumbnail changed, etc.)
  updateBoardInProjects: (board: BoardSchemeInterface) => {
    set((state) => {
      if (!board.associatedProjectId) return state;
      
      // Update the board in its associated project
      const projects = state.projects.map(project => {
        if (project.id === board.associatedProjectId) {
          const boards = project.boards || [];
          const updatedBoards = boards.map(b => b.id === board.id ? board : b);
          
          // If board doesn't exist in the project, add it
          if (!boards.some(b => b.id === board.id)) {
            updatedBoards.push(board);
          }
          
          return { ...project, boards: updatedBoards };
        }
        return project;
      });
      
      return {
        projects,
        ...deriveProjectLists(projects, state.loadedUid, state.sharedProjectIds),
      };
    });
    if (board.associatedProjectId) upsertBoardInDetails(board);
  },
  
  // Remove a board from all projects (for when board is deleted or association removed)
  removeBoardFromProjects: (boardId: string) => {
    set((state) => {
      // Remove the board from all projects
      const projects = state.projects.map(project => {
        if (project.boards && project.boards.some(b => b.id === boardId)) {

          const updatedBoards = project.boards.filter(b => b.id !== boardId);

          return {
            ...project,
            boards: updatedBoards
          };
        }
        return project;
      });
      
      return {
        projects,
        ...deriveProjectLists(projects, state.loadedUid, state.sharedProjectIds),
      };
    });
    removeBoardFromDetails(boardId);
  },

  // Update a collection in projects (for when collection is renamed, modified, etc.)
  updateCollectionInProjects: (collection: CollectionInterface) => {
    set((state) => {
      if (!collection.associatedProjectId) return state;
      
      // Update the collection in the specified project
      const projects = state.projects.map(project => {
        if (project.id === collection.associatedProjectId) {
          const collections = project.collections || [];
          const updatedCollections = collections.map(c => 
            c.id === collection.id ? collection : c
          );
          
          // If collection doesn't exist in the project, add it
          if (!collections.some(c => c.id === collection.id)) {
            updatedCollections.push(collection);
          }
          
          syncCollectionsToDetails(project.id, updatedCollections);
          return { ...project, collections: updatedCollections };
        }
        return project;
      });
      
      return {
        projects,
        ...deriveProjectLists(projects, state.loadedUid, state.sharedProjectIds),
      };
    });
  },

  // Remove a collection from all projects (for when collection is deleted)
  removeCollectionFromProjects: (collectionId: string) => {
    set((state) => {
      // Remove the collection from all projects that contain it
      const projects = state.projects.map(project => {
        if (project.collections && project.collections.some(c => c.id === collectionId)) {
          const updatedCollections = project.collections.filter(c => c.id !== collectionId);
          syncCollectionsToDetails(project.id, updatedCollections);
          
          return {
            ...project,
            collections: updatedCollections
          };
        }
        return project;
      });
      
      return {
        projects,
        ...deriveProjectLists(projects, state.loadedUid, state.sharedProjectIds),
      };
    });
  }


}));

// The team scope each project's details were fetched with: a flag that lands
// later must refetch rather than hit the per-project cache.
const loadedScope = new Map<string, string>();

export const useProjectSchemaDetails = create<ProjectSchemaDetailsState>((set, get) => ({
  projectsData: {},
  loadedUids: {},
  userProjects: [],
  ownedProjects: [],
  sharedProjects: [],

  setProject: (projectId: string, project: ProjectSchemeInterface) => set((state) => {
    // Update ownedProjects and sharedProjects arrays for the dropdown
    const ownedProjects = state.ownedProjects.map(p => p.id === projectId ? project : p);
    const sharedProjects = state.sharedProjects.map(p => p.id === projectId ? project : p);
    
    return {
      projectsData: {
        ...state.projectsData,
        [projectId]: {
          ...(state.projectsData[projectId] || { boards: [], collections: [], isLoading: false }),
          project
        }
      },
      ownedProjects,
      sharedProjects
    };
  }),

  setBoards: async (projectId: string, newBoards: BoardSchemeInterface[], uid: string, permissions: Permissions | null) => {
    // If no uid provided or projectId is missing, return
    if (!projectId || !uid) {
      console.error('Missing required parameters for setBoards: projectId or uid');
      return;
    }
    
    const state = get();
    
    // If this project doesn't exist in the state yet, return
    if (!state.projectsData[projectId]) {
      console.warn(`Project with ID ${projectId} not found in state.`);
      return;
    }
    
    try {
      // Since boards are now associated via associatedProjectId field,
      // we don't need to update the project document anymore
      // Just update local state
      set((state) => ({
        projectsData: {
          ...state.projectsData,
          [projectId]: {
            ...state.projectsData[projectId],
            // Only keep non-archived boards in the state
            boards: newBoards.filter(board => board.archive !== true)
          }
        }
      }));
      
      // Also update the main project store
      const projectStore = useProjectSchemas.getState();
      const projects = projectStore.projects.map(p => 
        p.id === projectId 
          ? { ...p, boards: newBoards.filter(board => board.archive !== true) }
          : p
      );
      
      useProjectSchemas.setState({
        projects,
        ...deriveProjectLists(projects, projectStore.loadedUid, projectStore.sharedProjectIds),
      });
      
    } catch (error) {
      console.error('Error updating boards for project:', error);
    }
  },

  // Re-pull this project's boards from Firestore and refresh the store. Board
  // edits in the /board editor (SaveButton) write the new screenshot + updatedAt
  // to Firestore ONLY, and getProjectDetails caches per (projectId, uid) for the
  // session — so without this, a revisit shows a stale board snapshot (missing
  // screenshot, frozen updatedAt). Only revalidates a COMPLETED load: gate on
  // loadedUids (stamped when getProjectDetails finishes), not projectsData —
  // getProjectDetails creates that entry synchronously (isLoading placeholder)
  // before its first await, so an entry-presence check passes on every first
  // visit and fires a duplicate boards fetch alongside the initial load.
  // setBoards filters archived + syncs both stores. The OrThrow variant is
  // load-bearing: the swallowing getBoardsForProject returns [] on a read
  // failure, which setBoards would treat as "no boards" and wipe the cache —
  // here a failure must skip setBoards and keep the cached snapshot.
  refreshBoards: async (projectId: string, uid: string, permissions: Permissions | null) => {
    if (!projectId || !uid || get().loadedUids[projectId] !== uid) return;
    try {
      if (!teamScopeKnown(uid)) await awaitActiveTeamScope(uid);
      const boards = await getBoardsForProjectOrThrow(
        projectId, uid, permissions,
        projectTeamScope(uid, get().projectsData[projectId]?.project),
      );
      await get().setBoards(projectId, boards, uid, permissions);
    } catch (error) {
      console.error('Error refreshing boards for project:', error);
    }
  },

  setCollections: async (projectId: string, newCollections: CollectionInterface[], uid: string, permissions: Permissions | null) => {
    // If no uid provided or projectId is missing, return
    if (!projectId || !uid) {
      console.error('Missing required parameters for setCollections: projectId or uid');
      return;
    }
    
    const state = get();
    
    // If this project doesn't exist in the state yet, return
    if (!state.projectsData[projectId]) {
      console.warn(`Project with ID ${projectId} not found in state.`);
      return;
    }

    try {
      // Update local state
      set((state) => ({
        projectsData: {
          ...state.projectsData,
          [projectId]: {
            ...state.projectsData[projectId],
            collections: newCollections
          }
        }
      }));

      // Also update the main project store
      const projectStore = useProjectSchemas.getState();
      const projects = projectStore.projects.map(p => 
        p.id === projectId 
          ? { ...p, collections: newCollections }
          : p
      );
      
      useProjectSchemas.setState({
        projects,
        ...deriveProjectLists(projects, projectStore.loadedUid, projectStore.sharedProjectIds),
      });

    } catch (error) {
      console.error('Error updating collections for project:', error);
    }
  },
  
  getProjectDetails: async (project: ProjectSchemeInterface, uid: string, permissions: Permissions | null) => {
    const { projectsData, loadedUids } = get();
    const projectId = project.id;
    
    if (!projectId || !uid) return;
    
    if (!teamScopeKnown(uid)) await awaitActiveTeamScope(uid);
    const teamScope = projectTeamScope(uid, project);
    const scopeKey = teamScope?.firmId ?? '';
    // Check if we already loaded this project with this uid and team scope
    if (loadedUids[projectId] === uid && projectsData[projectId] && loadedScope.get(projectId) === scopeKey) {
      // Data already loaded for this project and uid
      return;
    }
    
    // If we're already loading this project, don't start another load
    if (projectsData[projectId]?.isLoading) return;
    
    try {
      // Set loading state for this specific project
      set((state) => ({
        projectsData: {
          ...state.projectsData,
          [projectId]: {
            ...(state.projectsData[projectId] || { 
              project: {} as ProjectSchemeInterface,
              boards: [],
              collections: []
            }),
            isLoading: true
          }
        }
      }));
      
      // Fetch boards using the new API that queries by associatedProjectId
      let boards: BoardSchemeInterface[] = [];
      boards = await getBoardsForProject(project.id, uid, permissions, teamScope);
      boards = boards.filter((board) => !board.archive);

      let collections: CollectionInterface[] = [];
      collections = await getCollectionsForProject(project.id, uid, permissions, teamScope);

      const projectSchemasState = useProjectSchemas.getState();
      let userProjects: ProjectSchemeInterface[] = [];
      let ownedProjects: ProjectSchemeInterface[] = [];
      let sharedProjects: ProjectSchemeInterface[] = [];

      // Check if projects are already loaded in useProjectSchemas
      if (projectSchemasState.loadedUid === uid && projectSchemasState.projectsLoaded) {
        // Use cached projects from useProjectSchemas
        userProjects = projectSchemasState.activeProjects;
        ownedProjects = projectSchemasState.ownedProjects.filter(p => !p.archive);
        sharedProjects = projectSchemasState.sharedProjects.filter(p => !p.archive);
      } else {
        // Not loaded yet: read the workspace's projects (the team's readable
        // ones, or the user's own in Personal).
        const scope = activeTeamScope(uid);
        const allProjects = await getProjectsScheme(uid, [], scope?.firmId ?? null, scope);
        const lists = deriveProjectLists(allProjects, uid, []);
        userProjects = lists.activeProjects.filter((project) => projectBelongsToActiveWorkspace(project, uid));
        ownedProjects = lists.ownedProjects;
        sharedProjects = [];
      }

      // Update state with loaded data for this specific project
      set((state) => ({
        projectsData: {
          ...state.projectsData,
          [projectId]: {
            project,
            boards,
            collections,
            isLoading: false
          }
        },
        loadedUids: {
          ...state.loadedUids,
          [projectId]: uid
        },
        userProjects,
        ownedProjects,
        sharedProjects
      }));
      loadedScope.set(projectId, scopeKey);
      // The team's flag landed mid-fetch: the query shape changed, so refetch.
      if ((projectTeamScope(uid, project)?.firmId ?? '') !== scopeKey) {
        get().getProjectDetails(project, uid, permissions);
      }
      
      // Also update the main project store's boards
      const projectStore = useProjectSchemas.getState();
      const projects = projectStore.projects.map(p => 
        p.id === projectId ? { ...p, boards } : p
      );
      
      useProjectSchemas.setState({
        projects,
        ...deriveProjectLists(projects, projectStore.loadedUid, projectStore.sharedProjectIds),
      });
      
    } catch (error) {
      console.error('Error fetching project details:', error);
      
      // Update error state for this specific project
      set((state) => ({
        projectsData: {
          ...state.projectsData,
          [projectId]: {
            ...(state.projectsData[projectId] || { 
              project: {} as ProjectSchemeInterface,
              boards: [],
              collections: []
            }),
            isLoading: false
          }
        }
      }));
    }
  },
  
  getProjectById: (projectId: string) => {
    const { projectsData } = get();
    return projectsData[projectId] || null;
  },

  // New method to sync user projects from useProjectSchemas
  syncUserProjects: () => {
    const projectSchemasState = useProjectSchemas.getState();
    if (projectSchemasState.projectsLoaded) {
      const activeProjects = projectSchemasState.activeProjects;
      const ownedProjects = projectSchemasState.ownedProjects.filter(p => !p.archive);
      const sharedProjects = projectSchemasState.sharedProjects.filter(p => !p.archive);
      set({
        userProjects: activeProjects,
        ownedProjects,
        sharedProjects
      });
    }
  }
}));

export const useProjectState = create<ProjectManagementState>((set) => ({
  selectedProject: null,
  designFirms: [],
  teamMembers: [],
  associatedProjectsList: [],
  error: null,

  setProjectData: (data) => set(data),
  
  resetProjectData: () =>
    set({
      selectedProject: null,
      designFirms: [],
      teamMembers: [],
      associatedProjectsList: [],
      error: null,
    }),
  
  fetchProjectDataById: async (id: string, uid: string, permissions: Permissions | null) => {
    try {
      // First, fetch the project to check ownership
      const selectedProject = await getProjectSchemeById(id);
      
      if (!selectedProject) {
        throw new Error("Project not found");
      }
      // Teams sharing: the project's team flag decides which check applies.
      await ensureSharingV2Flag(firebaseHelper.db, selectedProject.design_firm_id);

      // Now check if user has access (either owner or has permissions)
      const hasAccess = hasProjectAccess(id, uid, permissions, selectedProject);

      if (!hasAccess) {
        throw new Error("You do not have permission to access this project");
      }
      teamAnalytics.itemOpened(
        teammateItem(
          "project",
          id,
          selectedProject,
          teammateAccess(selectedProject, !!permissions?.projectWrite?.includes(id)),
        ),
      );

      const [designFirms, associatedProjectsList] = await Promise.all([
        getDesignFirmsOfUser(uid, firebaseHelper.db, true),
        getAssociatedProjects(id),
      ]);

      // The project's own team names its owners and sharers; a Personal
      // project keeps the first team's roster.
      const rosterFirmId = selectedProject.design_firm_id || designFirms?.[0];
      let teamMembers: any[] = [];
      if (rosterFirmId) {
        teamMembers = await getDesignersWithAccess(rosterFirmId, firebaseHelper.db, true);
      }

      set({
        selectedProject,
        designFirms,
        teamMembers,
        associatedProjectsList,
      });
    } catch (error) {
      console.error("Error fetching project state:", error);
      set({
        error: error instanceof Error ? error.message : "Error fetching project data, please try again or contact studio@mattoboard.com"
      });
    }
  },
}));

// Re-derive owned/active project lists when the active workspace changes so
// switching in the bottom-left switcher immediately updates the Projects tab
// without a refetch (Phase A.3.3).
// On workspace switch the team vs. personal query differs (owner-scoped vs
// design_firm_id-scoped), so the previously-fetched cache is the wrong set —
// a team needs teammates' projects that were never fetched. Invalidate and
// refetch with the now-active query.
const refetchProjects = () => {
  const s = useProjectSchemas.getState();
  const uid = s.loadedUid;
  if (!uid || !s.projectsLoaded) return; // not active yet
  // Reset the load guard so getProjectSchemas (skipped when uid===loadedUid)
  // re-runs for the new workspace.
  useProjectSchemas.setState({
    projectsLoaded: false,
    loadedUid: null,
    isProjectsLoading: false,
  });
  // Fire-and-forget; getProjectSchemas catches its own errors, but guard the
  // promise so a refetch failure can never surface as an unhandled rejection.
  s.getProjectSchemas(uid, s.sharedProjectIds).catch(() => {});
};

useWorkspaceStore.subscribe((state, prev) => {
  if (state.activeIdByUid === prev.activeIdByUid) return;
  refetchProjects();
});

// The team's sharingV2 flag can arrive after the first load: the query shape
// changed, so refetch.
onSharingV2Flip(() => getActiveDesignFirmId(useProjectSchemas.getState().loadedUid), refetchProjects);